import cn from 'classnames';
import React, { Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toggleUiElement } from '../../store/actions/shell';
import { selectIs2FADrawerShown } from '../../store/selectors/shell';
import { OneLinkStatusEnum } from '../../types/oneLink';
import { IToggleRef } from '../../types/shell';
import { ActionButton } from '../forms';
import { TwoFASidebar } from '../index';
import Loader from '../Loader/Loader';

import styles from './TwoFaSidebarToggle.module.scss';

const TwoFASidebarContainer = React.lazy(
  () => import(/* webpackChunkName: "sidebar-twofa" */ '../../containers/TwoFASidebarContainer/TwoFASidebarContainer'),
);

interface IProps {
  className?: string;
  oneLinkId?: string;
  count?: number;
  disabled?: boolean;
  onClick?: () => void;
  status?: OneLinkStatusEnum;
  hideControl?: boolean;
}

const TwoFaSidebarToggle = React.forwardRef(
  (
    { className, disabled, count = 0, oneLinkId, onClick, status, hideControl }: IProps,
    ref: React.ForwardedRef<IToggleRef>,
  ) => {
    const dispatch = useDispatch();
    const isSidebarOpened = useSelector(selectIs2FADrawerShown);

    const handleToggleSidebar = React.useCallback(
      (flag?: boolean) => {
        dispatch(toggleUiElement({ twoFASidenavIsShown: flag }));
      },
      [dispatch],
    );

    const handleClick = React.useCallback(() => {
      handleToggleSidebar(!isSidebarOpened);
      if (onClick) {
        onClick();
      }
    }, [onClick, handleToggleSidebar, isSidebarOpened]);

    React.useImperativeHandle(ref, () => ({
      open: () => handleToggleSidebar(true),
      close: () => handleToggleSidebar(false),
      toggle: () => handleToggleSidebar(!isSidebarOpened),
      isOpened: isSidebarOpened,
    }));

    return (
      <>
        {!hideControl && (
          <ActionButton
            inline
            title={`${count} recipient${count === 1 ? '' : 's'}`}
            disabled={disabled}
            onClick={handleClick}
            className={cn(styles.container, className)}
          />
        )}
        <TwoFASidebar>
          {({ onClose }) => (
            <Suspense fallback={<Loader isLoading />}>
              <TwoFASidebarContainer status={status} oneLinkId={oneLinkId} onClose={onClose} />
            </Suspense>
          )}
        </TwoFASidebar>
      </>
    );
  },
);

export default TwoFaSidebarToggle;
